// The orders list of /commerce/orders: exact search, source / status / problem
// filters (in the URL, owned by CommerceOrders), the table, and the order
// drawer — parcels with their monitoring state, open parcel problems to mark
// as handled, a tracking number to add (written on the shop's order when the
// shop allows it), customer, delivery address and items.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { SiPrestashop, SiShopify } from 'react-icons/si';
import {
    ChevronRight, CircleAlert, CircleCheck, Clock, Code2, ExternalLink, Package, Plus, Search, ShoppingCart, TriangleAlert, X,
} from 'lucide-react';
import {
    ORDER_STATUSES, PARCEL_TONE, SOURCES, STATUS_TONE, fail, formatMoney, formatWhen, get, post,
} from './api';
import { SOURCE_LABEL, badgeTone } from './commerceAttention';
import { formatShortDate, formatShortTime } from '../../utils/dates';
import {
    Badge, Banner, DescriptionList, Drawer, EmptyState, Pagination, Switch,
} from '../../design/ui';

const PAGE_SIZE = 25;
const SHOP_SOURCES = ['shopify', 'prestashop'];

// The order's origin: the shop's own mark (third-party logo colours) or the API.
export const SourceBadge = ({ source }) => {
    const { t } = useTranslation();
    const label = t(`commerceOrders.sources.${source}`, SOURCE_LABEL[source] || source);
    return (
        <span className="co-v2__source">
            {source === 'shopify' && <SiShopify color="#95BF47" aria-hidden="true" />}
            {source === 'prestashop' && <SiPrestashop color="#DF0067" aria-hidden="true" />}
            {source !== 'shopify' && source !== 'prestashop' && <Code2 strokeWidth={1.8} aria-hidden="true" />}
            {label}
        </span>
    );
};

// Kept for the tabs that import it: a state badge from the api.js tones.
export const Chip = ({ tone = 'muted', children, title }) => (
    <Badge tone={badgeTone(tone)} title={title}>{children}</Badge>
);

const PUSH_TONE = { ok: 'success', wait: 'neutral', ko: 'danger' };

const PushLine = ({ tone, children, title }) => (
    <p className={`co-v2__push co-v2__push--${PUSH_TONE[tone] || 'neutral'}`} title={title} role="status">
        {tone === 'ok' ? <CircleCheck aria-hidden="true" /> : tone === 'ko' ? <CircleAlert aria-hidden="true" /> : <Clock aria-hidden="true" />}
        <span>{children}</span>
    </p>
);

// A parcel shipped for this order (tracking number). On a shop order whose
// shop writes tracking numbers, it is also written on the shop's order.
const AddTracking = ({ order, onAdded }) => {
    const { t } = useTranslation();
    const [form, setForm] = useState({ tracking_number: '', carrier: '', notify_customer: true });
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const shopOrder = SHOP_SOURCES.includes(order.source);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMessage(null);
        try {
            const res = await post(`/orders/${order.id}/shipments`, {
                tracking_number: form.tracking_number.trim(), carrier: form.carrier.trim() || null,
                notify_customer: shopOrder ? form.notify_customer : false,
            });
            const push = res.data.shop_push;
            setMessage(push === 'queued'
                ? { tone: 'ok', text: t('commerceOrders.trackingQueued', 'Ajouté ; le numéro est en cours d’inscription sur la commande {{source}}.', { source: t(`commerceOrders.sources.${order.source}`, SOURCE_LABEL[order.source] || order.source) }) }
                : push === 'tracking_disabled'
                    ? { tone: 'wait', text: t('commerceOrders.trackingDisabled', 'Ajouté ici. L’inscription sur la commande de la boutique n’est pas activée (page Boutiques).') }
                    : { tone: 'ok', text: t('commerceOrders.trackingAdded', 'Colis ajouté.') });
            setForm({ tracking_number: '', carrier: '', notify_customer: form.notify_customer });
            onAdded(res.data.order);
        } catch (err) {
            setMessage({ tone: 'ko', text: fail(err, t('commerceOrders.trackingError', 'Le colis n’a pas pu être ajouté.')) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <form className="co-v2__add-tracking" onSubmit={submit}>
            <span className="u-overline">{t('ordersMoneyUi.orders.addParcel', 'Ajouter un colis expédié')}</span>
            <div className="co-v2__add-tracking-fields">
                <div className="u-field">
                    <label className="u-label" htmlFor={`co-tn-${order.id}`}>{t('commerceOrders.trackingNumber', 'Numéro de suivi')}</label>
                    <input id={`co-tn-${order.id}`} className="u-input" value={form.tracking_number} maxLength={64}
                        onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} />
                </div>
                <div className="u-field">
                    <label className="u-label" htmlFor={`co-carrier-${order.id}`}>{t('commerceOrders.carrier', 'Transporteur')}</label>
                    <input id={`co-carrier-${order.id}`} className="u-input" value={form.carrier} maxLength={64}
                        onChange={(e) => setForm({ ...form, carrier: e.target.value })} placeholder="Colissimo" />
                </div>
                <button type="submit" className="u-btn u-btn--secondary" disabled={busy || form.tracking_number.trim().length < 4}>
                    <Plus aria-hidden="true" />
                    {t('commerceOrders.addTracking', 'Ajouter le suivi')}
                </button>
            </div>
            {shopOrder && (
                <Switch
                    id={`co-notify-${order.id}`}
                    checked={form.notify_customer}
                    onChange={(checked) => setForm({ ...form, notify_customer: checked })}
                    label={t('commerceOrders.notifyCustomer', 'La boutique prévient son client (e-mail d’expédition)')}
                />
            )}
            {message && <PushLine tone={message.tone}>{message.text}</PushLine>}
        </form>
    );
};

const ShopPush = ({ push, source }) => {
    const { t } = useTranslation();
    if (!push) return null;
    const shop = t(`commerceOrders.sources.${source}`, SOURCE_LABEL[source] || source);
    if (push.status === 'done') return <PushLine tone="ok">{t('commerceOrders.pushDone', 'Inscrit sur la commande {{shop}}', { shop })}</PushLine>;
    if (push.status === 'failed' || push.status === 'retrying') {
        return (
            <PushLine tone="ko" title={push.error || ''}>
                {push.status === 'failed'
                    ? t('commerceOrders.pushFailed', 'Non inscrit sur la commande {{shop}} : {{error}}', { shop, error: push.error || '—' })
                    : t('commerceOrders.pushRetrying', 'Nouvel essai d’inscription sur {{shop}} : {{error}}', { shop, error: push.error || '—' })}
            </PushLine>
        );
    }
    if (push.status === 'skipped') return <PushLine tone="wait">{t('commerceOrders.pushSkipped', 'Non inscrit sur la boutique (usage désactivé).')}</PushLine>;
    return <PushLine tone="wait">{t('commerceOrders.pushQueued', 'Inscription sur la commande {{shop}} en cours…', { shop })}</PushLine>;
};

const parcelTone = (monitoring) => (monitoring.flagged && monitoring.alert_state !== 'resolved' ? 'danger' : PARCEL_TONE[monitoring.status]);

const OrderDrawer = ({ orderId, onClose, onChanged }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const [order, setOrder] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setOrder(null);
        get(`/orders/${orderId}`)
            .then((res) => { if (!cancelled) setOrder(res.data); })
            .catch((err) => { if (!cancelled) setError(fail(err, t('commerceOrders.detailError', 'Impossible de charger la commande.'))); });
        return () => { cancelled = true; };
    }, [orderId, t]);

    const clearProblem = async (trackingNumber) => {
        setBusy(true);
        setError('');
        try {
            const res = await post(`/orders/${orderId}/problems/clear`, { tracking_number: trackingNumber || null });
            setOrder(res.data);
            onChanged();
        } catch (err) {
            setError(fail(err, t('commerceOrders.clearError', 'Impossible de marquer le problème comme traité.')));
        } finally {
            setBusy(false);
        }
    };

    const customer = order?.customer || {};
    const address = order?.shipping_address || {};
    const openProblems = (order?.problems || []).filter((p) => !p.cleared_at);
    const addressLines = [address.name, address.company, address.street, [address.postcode, address.city].filter(Boolean).join(' '), address.country]
        .filter(Boolean);

    return (
        <Drawer
            wide
            icon={ShoppingCart}
            title={order ? t('ordersMoneyUi.orders.drawerTitle', 'Commande {{number}}', { number: order.number }) : t('commerceOrders.detailTitle', 'Commande')}
            description={order ? t('ordersMoneyUi.orders.drawerDesc', 'Passée le {{placed}} · mise à jour le {{updated}}', {
                placed: formatWhen(order.placed_at, lang), updated: formatWhen(order.updated_at, lang),
            }) : null}
            onClose={onClose}
            closeLabel={t('commerceOrders.close', 'Fermer')}
        >
            {error && <Banner tone="danger">{error}</Banner>}
            {!order && !error && (
                <div className="co-v2__drawer-skeleton" aria-busy="true">
                    <span className="u-skeleton" />
                    <span className="u-skeleton" />
                    <span className="u-skeleton" />
                </div>
            )}

            {order && (
                <div className="co-v2__drawer">
                    <div className="co-v2__drawer-badges">
                        <SourceBadge source={order.source} />
                        <Chip tone={STATUS_TONE[order.status]}>{t(`commerceOrders.statuses.${order.status}`, order.status)}</Chip>
                        {order.source_status && <span className="u-muted co-v2__small">{order.source_status}</span>}
                        <span className="co-v2__drawer-total">{formatMoney(order.total, order.currency, lang)}</span>
                    </div>

                    {openProblems.length > 0 && (
                        <section className="co-v2__problems" aria-label={t('commerceOrders.problemsTitle', 'Problèmes colis en cours')}>
                            <div className="co-v2__problems-head">
                                <TriangleAlert aria-hidden="true" />
                                {t('commerceOrders.problemsTitle', 'Problèmes colis en cours')}
                            </div>
                            <ul className="u-list">
                                {openProblems.map((p) => (
                                    <li key={`${p.tracking_number}-${p.kind}`} className="u-row">
                                        <div className="u-row__main">
                                            <div className="u-row__title">
                                                <Badge tone="danger" dot>{t(`commerceOrders.problems.${p.kind}`, p.kind)}</Badge>
                                            </div>
                                            <div className="u-row__meta u-row__meta--wrap">
                                                <span className="co-v2__mono">{p.tracking_number}</span>
                                                {p.carrier ? ` · ${p.carrier}` : ''} · {formatWhen(p.detected_at, lang)}
                                                {p.detail && <> · « {p.detail} »</>}
                                            </div>
                                            {p.note && (
                                                <PushLine tone={p.note.status === 'written' ? 'ok' : 'ko'}>
                                                    {p.note.status === 'written'
                                                        ? t('commerceOrders.noteWritten', 'Note ajoutée sur la commande {{source}}', { source: t(`commerceOrders.sources.${order.source}`, SOURCE_LABEL[order.source] || order.source) })
                                                        : t('commerceOrders.noteError', 'Note non écrite : {{error}}', { error: p.note.error || '—' })}
                                                </PushLine>
                                            )}
                                        </div>
                                        <div className="u-row__side">
                                            <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy}
                                                onClick={() => clearProblem(p.tracking_number)}>
                                                <CircleCheck aria-hidden="true" />
                                                {t('commerceOrders.markHandled', 'Marquer comme traité')}
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <section className="co-v2__section">
                        <h3 className="u-overline">{t('commerceOrders.parcelsTitle', 'Colis')}</h3>
                        {(order.shipments || []).length === 0 ? (
                            <p className="u-muted co-v2__small">{t('commerceOrders.noShipment', 'Aucun numéro de suivi sur cette commande pour l’instant.')}</p>
                        ) : (
                            <ul className="u-list co-v2__parcels">
                                {order.shipments.map((s) => {
                                    const m = s.monitoring;
                                    return (
                                        <li key={s.tracking_number} className="u-row">
                                            <span className="u-icon-chip u-icon-chip--sm u-icon-chip--neutral" aria-hidden="true"><Package strokeWidth={1.8} /></span>
                                            <div className="u-row__main">
                                                <div className="u-row__title">
                                                    <span className="u-code">{s.tracking_number}</span>
                                                    {s.carrier && <span className="co-v2__carrier" title={s.carrier}>{s.carrier_ref?.service_label || s.carrier}</span>}
                                                    {m ? (
                                                        <Chip tone={parcelTone(m)}>{t(`commerceOrders.parcelStatuses.${m.status}`, m.status)}</Chip>
                                                    ) : (
                                                        <Chip tone="muted" title={t('commerceOrders.notMonitoredHint', 'Activez la surveillance colis pour suivre ce colis.')}>
                                                            {t('commerceOrders.notMonitored', 'Non surveillé')}
                                                        </Chip>
                                                    )}
                                                </div>
                                                {m?.last_event && (
                                                    <div className="u-row__meta u-row__meta--wrap">{m.last_event} · {formatWhen(m.last_event_at, lang)}</div>
                                                )}
                                                <ShopPush push={s.shop_push} source={order.source} />
                                            </div>
                                            {m && (
                                                <div className="u-row__side">
                                                    <Link className="u-btn u-btn--ghost u-btn--sm" to="/parcel-monitor">
                                                        {t('ordersMoneyUi.orders.openParcel', 'Surveillance')}
                                                        <ExternalLink aria-hidden="true" />
                                                    </Link>
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                        <AddTracking order={order} onAdded={(next) => { setOrder(next); onChanged(); }} />
                    </section>

                    <section className="co-v2__section co-v2__people">
                        <div>
                            <h3 className="u-overline">{t('commerceOrders.customerTitle', 'Client')}</h3>
                            <DescriptionList
                                items={[
                                    { label: t('commerceOrders.clientRef', 'Client (B2B)'), value: order.client_ref },
                                    { label: t('commerceOrders.name', 'Nom'), value: customer.name || '—' },
                                    { label: t('commerceOrders.email', 'E-mail'), value: customer.email || '—' },
                                    { label: t('commerceOrders.phone', 'Téléphone'), value: customer.phone || '—' },
                                ]}
                            />
                        </div>
                        <div>
                            <h3 className="u-overline">{t('commerceOrders.addressTitle', 'Livraison')}</h3>
                            <p className="co-v2__address">
                                {addressLines.length ? addressLines.map((line) => <span key={line}>{line}</span>) : <span className="u-muted">—</span>}
                            </p>
                        </div>
                    </section>

                    <section className="co-v2__section">
                        <h3 className="u-overline">{t('commerceOrders.itemsTitle', 'Articles')}</h3>
                        {(order.items || []).length === 0 ? <p className="u-muted co-v2__small">—</p> : (
                            <table className="co-v2__items">
                                <tbody>
                                    {order.items.map((item, i) => (
                                        <tr key={`${item.sku}-${i}`}>
                                            <td>
                                                {item.name || item.sku || '—'}
                                                {item.sku && item.name && <span className="u-cell-sub">{item.sku}</span>}
                                            </td>
                                            <td className="co-v2__num">× {item.quantity}</td>
                                            <td className="co-v2__num">{formatMoney(item.unit_price, order.currency, lang)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td colSpan={2}>{t('commerceOrders.total', 'Total')}</td>
                                        <td className="co-v2__num">{formatMoney(order.total, order.currency, lang)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        )}
                    </section>

                    <p className="u-muted co-v2__small co-v2__meta">
                        {t('commerceOrders.externalId', 'ID source')} <span className="u-code">{order.external_id}</span>
                    </p>
                </div>
            )}
        </Drawer>
    );
};

const OrdersTab = ({ filters, onFilter, onGoTo, refreshKey, onChanged, openId, onOpen }) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language || 'fr';
    const navigate = useNavigate();
    const [query, setQuery] = useState(filters.q);
    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const { q, source, status, problem } = filters;

    useEffect(() => { setQuery(q); }, [q]);
    useEffect(() => { setPage(1); }, [q, source, status, problem]);

    const load = useCallback(async () => {
        setError('');
        try {
            const params = { page, size: PAGE_SIZE };
            if (q) params.q = q;
            if (source) params.source = source;
            if (status) params.status = status;
            if (problem) params.problem = true;
            const res = await get('/orders', params);
            setData(res.data);
        } catch (err) {
            setError(fail(err, t('commerceOrders.listError', 'Impossible de charger les commandes.')));
            setData({ items: [], total: 0 });
        }
    }, [q, source, status, problem, page, t]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const items = data?.items || [];
    const hasFilters = Boolean(q || source || status || problem);

    return (
        <section className="u-card">
            <form className="u-toolbar co-v2__toolbar" onSubmit={(e) => { e.preventDefault(); onFilter({ q: query.trim() }); }}>
                <div className="u-input-group u-input-group--lead">
                    <Search className="u-input-group__icon" aria-hidden="true" />
                    <input
                        className="u-input"
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder={t('commerceOrders.searchPlaceholder', 'N° de commande, n° de suivi ou e-mail')}
                        aria-label={t('commerceOrders.search', 'Recherche exacte')}
                        title={t('ordersMoneyUi.orders.searchHint', 'Recherche exacte : appuyez sur Entrée')}
                    />
                </div>
                <select className="u-select" value={source} onChange={(e) => onFilter({ source: e.target.value })}
                    aria-label={t('commerceOrders.source', 'Source')}>
                    <option value="">{t('ordersMoneyUi.orders.allSources', 'Toutes les sources')}</option>
                    {SOURCES.map((s) => <option key={s} value={s}>{t(`commerceOrders.sources.${s}`, SOURCE_LABEL[s] || s)}</option>)}
                </select>
                <select className="u-select" value={status} onChange={(e) => onFilter({ status: e.target.value })}
                    aria-label={t('commerceOrders.status', 'Statut')}>
                    <option value="">{t('ordersMoneyUi.orders.allStatuses', 'Tous les statuts')}</option>
                    {ORDER_STATUSES.map((s) => <option key={s} value={s}>{t(`commerceOrders.statuses.${s}`, s)}</option>)}
                </select>
                <Switch
                    id="co-only-problems"
                    checked={problem}
                    onChange={(checked) => onFilter({ problem: checked })}
                    label={t('commerceOrders.onlyProblems', 'Problèmes colis uniquement')}
                />
                <span className="u-toolbar__spacer" />
                {hasFilters && (
                    <button type="button" className="u-btn u-btn--ghost u-btn--sm"
                        onClick={() => { setQuery(''); onFilter({ q: null, source: null, status: null, problem: null }); }}>
                        <X aria-hidden="true" />
                        {t('ordersMoneyUi.clearFilters', 'Effacer les filtres')}
                    </button>
                )}
            </form>

            {error && <div className="co-v2__pad"><Banner tone="danger">{error}</Banner></div>}

            {data === null ? (
                <div className="co-v2__skeleton" aria-hidden="true">{Array.from({ length: 6 }, (_, i) => <span key={i} className="u-skeleton" />)}</div>
            ) : items.length === 0 ? (
                hasFilters ? (
                    <EmptyState icon={Search} title={t('commerceOrders.emptyFiltered', 'Aucune commande ne correspond à cette recherche.')}>
                        {t('ordersMoneyUi.orders.emptyFilteredHint', 'La recherche est exacte : numéro de commande, numéro de suivi ou e-mail complet.')}
                    </EmptyState>
                ) : (
                    <EmptyState
                        icon={ShoppingCart}
                        title={t('commerceOrders.emptyTitle', 'Aucune commande pour l’instant')}
                        action={(
                            <div className="co-v2__empty-actions">
                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => navigate('/shops?add=1')}>
                                    {t('commerceOrders.connectShop', 'Connecter une boutique')}
                                </button>
                                <button type="button" className="u-btn u-btn--secondary u-btn--sm" onClick={() => onGoTo('api')}>
                                    {t('commerceOrders.useApi', 'Utiliser l’API')}
                                </button>
                            </div>
                        )}
                    >
                        {t('commerceOrders.emptyHint', 'Connectez une boutique Shopify ou PrestaShop, ou envoyez vos commandes par l’API : leurs colis sont surveillés et vous êtes prévenu au moindre problème.')}
                    </EmptyState>
                )
            ) : (
                <div className="u-table-scroll">
                    <table className="u-table co-v2__table">
                        <thead>
                            <tr>
                                <th style={{ width: '12%' }}>{t('commerceOrders.colOrder', 'Commande')}</th>
                                <th style={{ width: '15%' }}>{t('commerceOrders.colCustomer', 'Client')}</th>
                                <th style={{ width: '14%' }}>{t('commerceOrders.colStatus', 'Statut')}</th>
                                <th style={{ width: '17%' }}>{t('commerceOrders.colParcels', 'Colis')}</th>
                                <th style={{ width: '18%' }}>{t('commerceOrders.colProblem', 'Problème')}</th>
                                <th style={{ width: '10%' }} className="co-v2__num">{t('commerceOrders.colTotal', 'Total')}</th>
                                <th style={{ width: '14%' }}>{t('commerceOrders.colUpdated', 'Mise à jour')}</th>
                                <th style={{ width: '62px' }} aria-label={t('ordersMoneyUi.open', 'Ouvrir')} />
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((o) => {
                                const open = (o.problems || []).filter((p) => !p.cleared_at);
                                const first = o.shipments?.[0];
                                const openProblem = open.length > 0
                                    ? `${t(`commerceOrders.problems.${open[0].kind}`, open[0].kind)}${open.length > 1 ? ` +${open.length - 1}` : ''}`
                                    : '';
                                return (
                                    <tr key={o.id} className="co-v2__row" tabIndex={0} onClick={() => onOpen(o.id)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') onOpen(o.id); }}>
                                        <td>
                                            <strong>{o.number}</strong>
                                            <span className="u-cell-sub"><SourceBadge source={o.source} /></span>
                                        </td>
                                        <td title={o.customer?.name || ''}>
                                            {o.customer?.name || <span className="u-muted">—</span>}
                                            {o.client_ref && <span className="u-cell-sub">{o.client_ref}</span>}
                                        </td>
                                        <td><Chip tone={STATUS_TONE[o.status]}>{t(`commerceOrders.statuses.${o.status}`, o.status)}</Chip></td>
                                        <td title={first?.tracking_number || ''}>
                                            {first ? (
                                                <>
                                                    <span className="co-v2__mono">{first.tracking_number}</span>
                                                    <span className="u-cell-sub">
                                                        {first.monitoring
                                                            ? t(`commerceOrders.parcelStatuses.${first.monitoring.status}`, first.monitoring.status)
                                                            : t('commerceOrders.notMonitored', 'Non surveillé')}
                                                        {o.shipments.length > 1 ? ` · +${o.shipments.length - 1}` : ''}
                                                    </span>
                                                </>
                                            ) : <span className="u-muted">—</span>}
                                        </td>
                                        <td title={openProblem}>
                                            {openProblem ? <Badge tone="danger" dot>{openProblem}</Badge> : <span className="u-muted">—</span>}
                                        </td>
                                        <td className="co-v2__num">{formatMoney(o.total, o.currency, lang)}</td>
                                        <td title={formatWhen(o.updated_at, lang)}>
                                            <span className="u-num">{formatShortDate(o.updated_at, lang)}</span>
                                            {o.updated_at && <span className="u-cell-sub u-num">{formatShortTime(o.updated_at, lang)}</span>}
                                        </td>
                                        <td className="co-v2__chevron"><ChevronRight aria-hidden="true" /></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {data && items.length > 0 && (
                <Pagination
                    page={page}
                    pageSize={PAGE_SIZE}
                    total={Number(data.total || 0)}
                    onChange={setPage}
                    summary={({ first, last, total }) => t('ordersMoneyUi.pageSummary', '{{first}}–{{last}} sur {{total}}', {
                        first, last, total: Number(total).toLocaleString(lang),
                    })}
                    previousLabel={t('ordersMoneyUi.previousPage', 'Page précédente')}
                    nextLabel={t('ordersMoneyUi.nextPage', 'Page suivante')}
                />
            )}

            {openId && <OrderDrawer orderId={openId} onClose={() => onOpen(null)} onChanged={() => { load(); onChanged(); }} />}
        </section>
    );
};

export default OrdersTab;
